import { useState } from "react";
import { MessageCircle, X, Send, Paperclip, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "../lib/api";
import { uploadImage } from "../lib/uploads";

export default function ChatbotWidget() {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("enquiry");
  const [form, setForm] = useState({ name: "", email: "", mobile: "", message: "", order_id: "" });
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const handleFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 4 * 1024 * 1024) return toast.error("Image must be under 4 MB");
    setUploading(true);
    try {
      // Public chatbot can't authenticate; if logged out, fall back to base64 (kept inline)
      // Try the authenticated upload endpoint first; on failure, base64 inline.
      try {
        const r = await uploadImage(f, "chatbot");
        setAttachment({ name: f.name, data: r.url, storage: r.storage });
      } catch {
        const reader = new FileReader();
        await new Promise((res) => { reader.onload = () => { setAttachment({ name: f.name, data: reader.result, storage: "base64" }); res(); }; reader.readAsDataURL(f); });
      }
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault(); setSending(true);
    try {
      await api.post("/chatbot/enquiry", { ...form, type, attachment_name: attachment?.name || "", attachment_data: attachment?.data || "" });
      setDone(true); toast.success("Your message has been received.");
    } catch (e) { toast.error(formatErr(e)); } finally { setSending(false); }
  };

  return (
    <>
      {/* Chatbot pill — intentionally positioned to overlay the Emergent badge */}
      <button
        data-testid="chatbot-toggle"
        onClick={() => setOpen(!open)}
        aria-label="Open support chat"
        className="chatbot-pill group fixed bottom-4 right-4 md:bottom-5 md:right-5 z-[2147483647] flex items-center gap-2 text-white rounded-full shadow-2xl transition-all"
      >
        <span className="chatbot-pill-icon flex items-center justify-center w-7 h-7 rounded-full bg-white/15 shrink-0">
          {open ? <X size={15}/> : <MessageCircle size={15}/>}
        </span>
        {!open && (
          <span className="chatbot-pill-label relative pr-1 text-[12px] font-semibold tracking-wide leading-none">
            <span className="chatbot-text-a">Need Help?</span>
            <span className="chatbot-text-b">Chatbot</span>
          </span>
        )}
      </button>
      {open && (
        <div data-testid="chatbot-panel" className="fixed bottom-20 right-4 md:bottom-24 md:right-6 z-[2147483646] w-[380px] max-w-[calc(100vw-2rem)] bg-white text-dark shadow-2xl border border-black/10 p-6 max-h-[80vh] overflow-y-auto">
          <h3 className="font-display text-xl text-dark mb-1">How can we help?</h3>
          <p className="text-xs text-gray-500 mb-5">Our concierge team responds within 24 hours.</p>
          {done ? (
            <div className="text-center py-8">
              <div className="text-copper font-display text-xl mb-2">Thank you</div>
              <p className="text-sm text-gray-600">Your message has been sent. We'll reach out soon.</p>
              <button onClick={() => { setDone(false); setForm({name:"",email:"",mobile:"",message:"",order_id:""}); setAttachment(null); }} className="mt-5 text-xs uppercase tracking-luxe text-copper hover:text-[#A0834F]">Send another</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="flex gap-2 mb-2">
                {["enquiry","refund","exchange"].map(t => {
                  const selected = type === t;
                  return (
                    <button
                      key={t}
                      type="button"
                      data-testid={`chatbot-type-${t}`}
                      onClick={() => setType(t)}
                      className={`text-[10px] uppercase tracking-luxe px-3 py-1.5 border transition-colors ${selected ? "bg-copper text-white border-copper hover:bg-[#A0834F]" : "bg-transparent text-current border-current/30 hover:border-copper hover:text-copper"}`}
                      style={selected ? { backgroundColor: "#B89766", color: "#fff", borderColor: "#B89766" } : undefined}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
              <input data-testid="chatbot-name" required placeholder="Full Name" className="input-box" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})}/>
              <input data-testid="chatbot-email" required type="email" placeholder="Email" className="input-box" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})}/>
              <input data-testid="chatbot-mobile" required placeholder="Mobile Number" className="input-box" value={form.mobile} onChange={(e)=>setForm({...form, mobile:e.target.value})}/>
              {(type === "refund" || type === "exchange") && <input data-testid="chatbot-order" placeholder="Order Number" className="input-box" value={form.order_id} onChange={(e)=>setForm({...form, order_id:e.target.value})}/>}
              <textarea data-testid="chatbot-message" required placeholder="Your message…" rows={4} className="input-box resize-none" value={form.message} onChange={(e)=>setForm({...form, message:e.target.value})}/>

              {/* Attach image */}
              <label data-testid="chatbot-attach" className="flex items-center justify-between gap-2 px-3 py-2 border border-dashed border-black/15 hover:border-copper text-xs uppercase tracking-luxe text-gray-700 cursor-pointer">
                <span className="flex items-center gap-2"><Paperclip size={12}/> {attachment ? attachment.name : "Attach image (optional)"}</span>
                {attachment && <button type="button" onClick={(e)=>{ e.preventDefault(); setAttachment(null); }} className="text-red-500"><X size={12}/></button>}
                <input type="file" accept="image/*" className="hidden" onChange={handleFile}/>
              </label>
              {attachment && <div className="aspect-video bg-[#FAF7F2] overflow-hidden"><img src={attachment.data} alt="attachment preview" className="w-full h-full object-contain"/></div>}

              <button data-testid="chatbot-submit" disabled={sending} type="submit" className="w-full bg-dark text-white font-medium uppercase tracking-luxe text-xs py-3 hover:bg-copper transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                <Send size={14}/> {sending? "Sending…":"Send message"}
              </button>
            </form>
          )}
        </div>
      )}
    </>
  );
}
